// The computers this person may drive, and the KVM boxes they are plugged into: a list of each, and a page for each
// one, opened by clicking its row, with a breadcrumb of the way there (Computers › Lab PC 2 › KVM1) like folders. A
// computer's page has its description, its ways in, and its passwords and secrets (the PC's sign-in, its RDP and VNC
// logins, values of its own); a KVM box's page has its description, its own login and the computers on it. Everything
// is edited in place: a name, value or description by clicking it (Enter saves, or Cmd/Ctrl+Enter in a description;
// Esc cancels), a way in or a share in a panel under its row. A secret shows as dots; its managers may show or copy it,
// one at a time, each written to the audit. Refreshes itself every 5 s while nothing is being edited.
import { Fragment, useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent as RMouseEvent, type ReactNode } from "react";
import { api, type Access, type ComputerT, type KvmT, type LearnedT, type Me, type PathT, type ShareT } from "./api";
import { ValueCells, type ValueSpec } from "./ComputerValues";

const KIND_LABEL: Record<string, string> = { kvm: "KVM", vnc: "VNC", rdp: "Remote Desktop", sim: "Simulator" };
const PATH_SECRET: Record<string, string> = { vnc: "vnc_password", rdp: "rdp_password" };
const DEFAULT_TYPING_MS = 20;
const ADDABLE: [string, string][] = [["kvm", "KVM"], ["vnc", "VNC"], ["rdp", "Remote Desktop"]];
const who = (email: string) => email.split("@")[0];
const clock = (at: number) => new Date(at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const day = (at: number) => new Date(at * 1000).toLocaleDateString([], { month: "short", day: "numeric" });
const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const VIEW_KEY = "computers.view";

type Tone = "ok" | "warn" | "err" | "idle";
export type Status = { tone: Tone; text: string; detail?: string; usable: boolean; refused?: boolean };

/** What happens if you click Connect, in words: free, yours, in use, off its KVM's cable, or the last failure. */
export function pathStatus(p: PathT, me: Me): Status {
  if (p.holder && p.holder !== me.email)
    return { tone: "warn", text: `in use · ${who(p.holder)}${p.via_holder === "mcp" ? " (Claude Code)" : ""}`, usable: false };
  if (p.holder) return { tone: "ok", text: "yours", usable: true };
  if (p.cable) return { tone: "warn", text: "off cable", detail: p.cable, usable: false };
  if (p.kvm_busy) return { tone: "warn", text: p.kvm_busy, usable: false };
  if (p.error) {
    const refused = /login refused/i.test(p.error.message);
    const detail = p.error.message.replace(/^RDP login refused:\s*/i, "").replace(/^RDP:\s*/, "");
    return { tone: "err", text: `${refused ? "login refused" : "failed"} · ${clock(p.error.at)}`, detail: sentence(detail),
      usable: true, refused };
  }
  return { tone: "ok", text: "free", usable: true };
}

function summary(c: ComputerT, me: Me): Status {
  const all = c.paths.map((p) => pathStatus(p, me));
  const held = all.find((s) => s.text === "yours" || s.text.startsWith("in use"));
  if (held) return { ...held, detail: undefined };
  const free = all.filter((s) => s.text === "free").length;
  if (free) return { tone: "ok", text: all.length === 1 ? "free" : `${free} free`, usable: true };
  if (!all.length) return { tone: "idle", text: "no way in yet", usable: false };
  return { ...all[0], detail: undefined };
}

function address(p: PathT, kvms: KvmT[]): string {
  if (p.kind === "kvm") return String(kvms.find((k) => k.id === p.kvm_id)?.settings.url ?? "").replace(/^https?:\/\//, "");
  const host = String(p.settings.host ?? "");
  return host && p.settings.port ? `${host}:${p.settings.port}` : host;
}

/** A way in's own settings in words: how fast it types (KVM), which account it signs in as (RDP). */
function wayFacts(p: PathT): string {
  if (p.kind === "kvm") return `typing ${String(p.settings.type_event_ms ?? DEFAULT_TYPING_MS)} ms per key`;
  if (p.kind === "rdp") return `as ${String(p.settings.user ?? "") || "no user"}`;
  return "";
}

/** What the harness learned on a way in, short ("learned Oct 27 · wheel reversed"), and in full for its tooltip. */
export function learnedText(l: LearnedT[] | undefined): [string, string] {
  if (!l?.length) return ["nothing learned yet", "The harness learns this PC's pointer and wheel on its first session."];
  const last = Math.max(...l.map((x) => Math.max(x.calibration ?? 0, x.tuning ?? 0)));
  const rev = l.some((x) => x.wheel === "reversed");
  const full = l.map((x) => `${x.screen}: ${[x.calibration && `pointer calibrated ${day(x.calibration)}`,
    x.tuning && `tuned ${day(x.tuning)}`, x.wheel && `wheel ${x.wheel}`].filter(Boolean).join(", ") || "seen"}`).join("\n");
  return [[last ? `learned ${day(last)}` : "", rev ? "wheel reversed" : ""].filter(Boolean).join(" · ") || "seen", full];
}

/** Everything stored for a computer, as its rows under "Passwords & secrets": the PC's sign-in for its system, its
 * RDP and VNC logins, then its own values (a KVM box's login is the box's, on its page). Only managers see the fixed
 * ones (which are set is theirs to know); everyone who may see the computer sees its own values, secrets as dots. */
function valueSpecs(c: ComputerT): ValueSpec[] {
  const manage = c.access === "manage";
  const fixedFetch = (k: string) => async (why: "show" | "copy") => (await api.computerValue(c.id, k, why)).value;
  const fixed = (k: string, label: string, usedBy: string): ValueSpec => ({
    key: `v:${c.id}:${k}`, label, name: k, usedBy, secret: true, set: c.secrets_set.includes(k), canEdit: true, canShow: true,
    fetch: fixedFetch(k), removeLabel: "Forget",
    save: ({ value }) => api.saveComputer({ id: c.id, name: c.name, os: c.os, secrets: { [k]: value } }),
    remove: () => api.removeValue(c.id, k) });
  const out: ValueSpec[] = [];
  if (manage) {
    if (c.os === "macos") out.push(fixed("mac_password", "Mac password", "unlocks the screen"));
    else out.push(fixed("win_pin", "Windows PIN", "unlocks the screen"),
      fixed("win_password", "Windows password", "unlocks the screen"));
    const rdp = c.paths.find((p) => p.kind === "rdp");
    if (c.paths.some((p) => p.kind === "vnc")) out.push(fixed("vnc_password", "VNC password", "signs in over VNC"));
    if (rdp) out.push(fixed("rdp_password", "RDP password",
      `signs in over Remote Desktop as ${String(rdp.settings.user ?? "") || "no user"}`));
  }
  for (const v of c.values ?? [])
    out.push({ key: `v:${c.id}:${v.name}`, label: v.name, name: v.name, own: true, secret: v.secret, set: true,
      plain: v.value, usedBy: v.secret ? "typed by the agent by name (never shown to it)" : "shown to the agent",
      canEdit: manage, canShow: manage,
      fetch: async (why) => (await api.computerValue(c.id, v.name, why)).value,
      save: ({ name, value, secret }) => api.saveValue(c.id, name, { value, secret, old: v.name }),
      remove: () => api.removeValue(c.id, v.name) });
  return out;
}

type PanelT =
  | { kind: "computer"; c?: ComputerT }                       // change system, or a new computer
  | { kind: "path"; c: ComputerT; p?: PathT; way: string }   // edit a way in, or add one of kind `way`
  | { kind: "merge"; c: ComputerT }
  | { kind: "split"; c: ComputerT; p: PathT }
  | { kind: "share"; what: "computer" | "kvm"; id: string; name: string; shares: ShareT[] }
  | { kind: "kvm"; k?: KvmT }
  | { kind: "onbox"; k: KvmT };                                // a computer on this KVM box: a new one, or one you have

/** The row a panel opens under. */
function anchor(pn: PanelT): string {
  switch (pn.kind) {
    case "computer": return pn.c ? `c:${pn.c.id}` : "new";
    case "path": return pn.p ? `p:${pn.p.id}` : `add:${pn.c.id}`;
    case "merge": return `c:${pn.c.id}`;
    case "split": return `p:${pn.p.id}`;
    case "share": return pn.what === "computer" ? `c:${pn.id}` : `k:${pn.id}`;
    case "kvm": return pn.k ? `k:${pn.k.id}` : "new";
    case "onbox": return `kadd:${pn.k.id}`;
  }
}

/** Where the screen is: the list of computers or of KVM boxes, and the pages opened from it, in order. */
type Crumb = { kind: "computer" | "kvm"; id: string };
export type Loc = { root: "computers" | "kvms"; trail: Crumb[] };
const PLACE_KEY = "computers.place";              // where it was, for this tab (back from a computer's screen)
const SHUT_KEY = "computers.shut";                 // the sections someone folded: "secrets", "login"
const PEEK_KEY = "computers.peek";                 // the rows someone opened in the lists: "c:<id>", "k:<id>"

function wayName(p: PathT, kvms: KvmT[]): string {
  return p.kind === "kvm" ? kvms.find((k) => k.id === p.kvm_id)?.name ?? p.via : KIND_LABEL[p.kind] ?? p.kind;
}
const firstLine = (s?: string) => (s ?? "").split("\n").find((x) => x.trim())?.trim() ?? "";

export function Computers({ me, current, onConnect, onRefresh }: {
  me: Me; current: string | null; onConnect: (id: string) => void; onRefresh: () => void;
}) {
  const [loc, setLoc] = useState<Loc>(() => {
    try {
      const here = sessionStorage.getItem(PLACE_KEY);
      if (here) return JSON.parse(here) as Loc;
      return { root: localStorage.getItem(VIEW_KEY) === "kvms" ? "kvms" : "computers", trail: [] };
    }
    catch { return { root: "computers", trail: [] }; }
  });
  const [comps, setComps] = useState<ComputerT[] | null>(null);
  const [kvms, setKvms] = useState<KvmT[]>([]);
  const [panel, setPanel] = useState<PanelT | null>(null);
  const [busy, setBusy] = useState(false);
  const [panelErr, setPanelErr] = useState("");
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<string | null>(null);   // what is being edited in place (a name, a value)
  const [draft, setDraft] = useState("");                        // a new name while renaming
  const [shut, setShut] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SHUT_KEY) ?? "[]") as string[]); } catch { return new Set(); }
  });
  const [peek, setPeek] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(PEEK_KEY) ?? "[]") as string[]); } catch { return new Set(); }
  });
  const list = useRef<HTMLDivElement>(null);

  const load = useCallback(() => Promise.all([api.computers(), api.kvms()])
    .then(([c, k]) => { setComps(c); setKvms(k); setErr(""); }).catch((e) => setErr(String(e.message))), []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (panel || editing) return;                       // editing: the row or panel keeps what it shows
    const t = window.setInterval(() => { void load(); }, 5000);
    return () => clearInterval(t);
  }, [load, panel, editing]);

  /** Show `next`: a list, or a page. The browser's Back goes back one step. */
  const go = useCallback((next: Loc, push = true) => {
    setLoc(next); setPanel(null); setEditing(null); setPanelErr(""); setQ("");
    if (push) try { history.pushState({ ...(history.state ?? {}), cloc: next }, ""); } catch { /* a convenience only */ }
    try { localStorage.setItem(VIEW_KEY, next.root); sessionStorage.setItem(PLACE_KEY, JSON.stringify(next)); }
    catch { /* a convenience only */ }
  }, []);
  useEffect(() => {
    try { history.replaceState({ ...(history.state ?? {}), cloc: loc }, ""); } catch { /* a convenience only */ }
    const back = (e: PopStateEvent) => { const l = (e.state as { cloc?: Loc } | null)?.cloc; if (l) go(l, false); };
    window.addEventListener("popstate", back);
    return () => window.removeEventListener("popstate", back);
  }, []);                                                // once: Back and Forward are the browser's from here on
  /** Open a computer or a KVM box from this page: deeper, or back to it if it is on the way here already. */
  const openItem = (c: Crumb) => {
    const i = loc.trail.findIndex((x) => x.kind === c.kind && x.id === c.id);
    go({ root: loc.root, trail: i >= 0 ? loc.trail.slice(0, i + 1) : [...loc.trail, c] });
  };
  // a page whose computer or box is gone (removed, merged, no longer shared): back to what is still there
  useEffect(() => {
    if (!comps) return;
    const there = (c: Crumb) => c.kind === "computer" ? comps.some((x) => x.id === c.id) : kvms.some((x) => x.id === c.id);
    const i = loc.trail.findIndex((c) => !there(c));
    if (i >= 0) go({ root: loc.root, trail: loc.trail.slice(0, i) }, false);
  }, [comps, kvms]);                                    // after each load

  const fold = (section: string) => setShut((s) => {
    const n = new Set(s); if (n.has(section)) n.delete(section); else n.add(section);
    try { localStorage.setItem(SHUT_KEY, JSON.stringify([...n])); } catch { /* a convenience only */ }
    return n;
  });
  const unfold = (section: string) => { if (shut.has(section)) fold(section); };
  const togglePeek = (key: string) => setPeek((s) => {
    const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key);
    try { localStorage.setItem(PEEK_KEY, JSON.stringify([...n])); } catch { /* a convenience only */ }
    return n;
  });
  const open = (pn: PanelT) => { setPanel(pn); setPanelErr(""); setEditing(null); };
  const edit = (key: string | null) => { setEditing(key); if (key) setPanel(null); };
  /** Something edited in place is done: say so beside it, and reload. */
  const done = (key: string, msg: string) => {
    setSaved(`${key}|${msg}`); window.setTimeout(() => setSaved((s) => (s === `${key}|${msg}` ? null : s)), 2000);
    void load().then(onRefresh);
  };
  const close = () => { setPanel(null); setPanelErr(""); };
  const flash = (key: string) => done(key, "Saved ✓");

  /** Do `work`; on success close the panel, show "Saved ✓" where it returns (or `key`), and reload. */
  const run = async (work: () => Promise<unknown>, key?: string) => {
    const inPanel = panel !== null;
    setBusy(true); setPanelErr("");
    try {
      const r = await work();
      setPanel(null);
      const k = typeof r === "string" ? r : key;
      if (k) flash(k); else { await load(); onRefresh(); }
    } catch (e) {
      (inPanel ? setPanelErr : setErr)(String((e as Error).message));
    } finally { setBusy(false); }
  };
  const confirmRun = (question: string, work: () => Promise<unknown>) => { if (confirm(question)) void run(work); };

  function renderPanel(pn: PanelT): ReactNode {
    const common = { busy, error: panelErr, onCancel: close };
    switch (pn.kind) {
      case "computer":
        return <ComputerForm {...common} c={pn.c} onSave={(name, os) => void run(async () => {
          const c = await api.saveComputer({ id: pn.c?.id, name, os, secrets: {} });
          if (!pn.c) { await load(); openItem({ kind: "computer", id: c.id }); }    // a new one: on to its ways in
          return `c:${c.id}`;
        })} />;
      case "path":
        return <PathForm {...common} c={pn.c} p={pn.p} way={pn.way} kvms={kvms} onSave={(kvmId, settings, secret) => void run(async () => {
          const t = await api.savePath(pn.c.id, { id: pn.p?.id, kind: pn.way, kvm_id: kvmId, settings });
          const sk = PATH_SECRET[pn.way];
          if (sk && secret) await api.saveComputer({ id: pn.c.id, name: pn.c.name, os: pn.c.os, secrets: { [sk]: secret } });
          return `p:${t.id}`;
        })} />;
      case "merge":
        return <MergeForm {...common} c={pn.c} comps={comps ?? []} onSave={(other) => void run(
          () => api.merge(pn.c.id, other), `c:${pn.c.id}`)} />;
      case "split":
        return <SplitForm {...common} p={pn.p} onSave={(name) => void run(async () => `c:${(await api.splitPath(pn.p.id, name)).id}`)} />;
      case "share":
        return <SharePanel panel={pn} onClose={() => { close(); void load(); }} onShares={(shares) => setPanel({ ...pn, shares })} />;
      case "onbox":
        return <AddToBox {...common} k={pn.k} mine={attachable(pn.k)}
          onNew={(name, os, onCable) => void run(async () => {
            const c = await api.saveComputer({ id: undefined, name, os, secrets: {} });
            try { await api.savePath(c.id, { id: undefined, kind: "kvm", kvm_id: pn.k.id, settings: {} }); }
            catch (e) { await api.removeComputer(c.id).catch(() => undefined); throw e; }   // no half-made computer
            if (onCable) await api.setCable(pn.k.id, c.id);
            return `kc:${pn.k.id}:${c.id}`;
          })}
          onAttach={(cid, onCable) => void run(async () => {
            await api.savePath(cid, { id: undefined, kind: "kvm", kvm_id: pn.k.id, settings: {} });
            if (onCable) await api.setCable(pn.k.id, cid);
            return `kc:${pn.k.id}:${cid}`;
          })} />;
      case "kvm":
        return <KvmForm {...common} k={pn.k} onSave={(name, settings, password) => void run(async () => {
          const k = await api.saveKvm({ id: pn.k?.id, name, settings, secrets: { password } });
          if (!pn.k) { await load(); openItem({ kind: "kvm", id: k.id }); }
          return `k:${k.id}`;
        })} />;
    }
  }
  const panelAt = (key: string) => panel && anchor(panel) === key
    ? <div className="cpanel-row">{renderPanel(panel)}</div> : null;
  const savedMsg = (key: string) => saved === key ? "Saved ✓" : saved?.startsWith(`${key}|`) ? saved.slice(key.length + 1) : null;
  const Saved = ({ k }: { k: string }) => savedMsg(k) ? <span className="csaved" role="status">{savedMsg(k)}</span> : null;

  /** A name edited in place: ✎ (or a double-click) turns it into a field, Enter saves, Esc cancels. */
  const nameEditor = (key: string, name: string, canEdit: boolean, save: (n: string) => Promise<unknown>) =>
    editing === key
      ? <input className="field cc-rename" aria-label={`New name for ${name}`} autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)} onBlur={() => setEditing(null)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
              const n = draft.trim();
              if (!n || n === name) { setEditing(null); return; }
              save(n).then(() => { setEditing(null); setErr(""); done(key, "Saved ✓"); })
                .catch((x) => setErr(String((x as Error).message)));
            } else if (e.key === "Escape") { e.preventDefault(); setEditing(null); }
          }} />
      : <>
        <h1 className="chero-name" onDoubleClick={canEdit ? () => { setDraft(name); edit(key); } : undefined}>{name}</h1>
        {canEdit && <button className="btn quiet cc-pen" aria-label={`Rename ${name}`} title="Rename"
          onClick={() => { setDraft(name); edit(key); }}>✎</button>}
      </>;

  const describe = (kind: "computer" | "kvm", id: string, text: string | undefined, canEdit: boolean, what: string) =>
    <Description text={text ?? ""} canEdit={canEdit} editing={editing === `d:${id}`} what={what}
      onEdit={(on) => edit(on ? `d:${id}` : null)} saved={savedMsg(`d:${id}`)}
      onSave={async (t) => { await api.describe(kind, id, t); done(`d:${id}`, "Saved ✓"); }} />;

  const managed = new Set((comps ?? []).filter((c) => c.access === "manage").map((c) => c.id));
  const top = loc.trail.at(-1);
  const crumbName = (c: Crumb) => (c.kind === "computer" ? comps?.find((x) => x.id === c.id)?.name
    : kvms.find((x) => x.id === c.id)?.name) ?? "…";
  const rootLabel = loc.root === "computers" ? "Computers" : "KVM boxes";

  // ------------------------------------------------------------------------------------------------- the lists
  /** Arrow keys move between the list's rows, Right and Left open and close a row's preview, Enter opens its page. */
  const rowKeys = (e: KeyboardEvent<HTMLDivElement>, onOpen: () => void, pk: string) => {
    if (e.target !== e.currentTarget) return;
    const items = [...(list.current?.querySelectorAll<HTMLElement>(".citem") ?? [])];
    const i = items.indexOf(e.currentTarget);
    if (e.key === "Enter") onOpen();
    else if (e.key === "ArrowDown") items[i + 1]?.focus();
    else if (e.key === "ArrowUp") items[i - 1]?.focus();
    else if (e.key === "ArrowRight") { if (!peek.has(pk)) togglePeek(pk); }
    else if (e.key === "ArrowLeft") { if (peek.has(pk)) togglePeek(pk); }
    else return;
    e.preventDefault();
  };
  /** The arrow at a list row's left: shows what is under it right there, without leaving the list. */
  const twist = (pk: string, name: string) => {
    const on = peek.has(pk);
    return <button className="ci-twist" aria-expanded={on} aria-label={`${on ? "Collapse" : "Expand"} ${name}`}
      title={on ? "Hide" : "Show it here"} onClick={() => togglePeek(pk)} />;
  };
  const attachable = (k: KvmT) => (comps ?? []).filter((c) => c.access === "manage"
    && !c.paths.some((p) => p.kind === "kvm" && p.kvm_id === k.id));

  /** A computer's ways in, each with Connect: its preview in the list. */
  const waysPeek = (c: ComputerT) => {
    const more = (c.description ?? "").trim();
    return <div className="cpeek">
      {more.includes("\n") && <div className="cpeek-desc">{more}</div>}
      <div className="ctree" role="grid" aria-label={`${c.name}'s ways in`}>
        {c.paths.map((p) => {
          const s = pathStatus(p, me);
          return <div key={p.id} role="row" className="crow">
            <div role="gridcell" className="cc-name">
              {p.kind === "kvm" && p.kvm_id
                ? <button className="clink cc-way" onClick={() => openItem({ kind: "kvm", id: p.kvm_id! })}>{wayName(p, kvms)} ›</button>
                : <span className="cc-way">{wayName(p, kvms)}</span>}</div>
            <div role="gridcell" className="cc-addr mono">{address(p, kvms)}</div>
            <div role="gridcell" className="cc-sign">{wayFacts(p)}</div>
            <div role="gridcell"><StatusCell s={s} /></div>
            <div role="gridcell" className="cc-acts">
              <button className={`btn ${s.usable ? "primary" : ""}`} aria-label={`Connect to ${c.name} via ${p.via}`}
                onClick={() => onConnect(p.id)}>Connect</button></div>
            {s.detail && <div role="gridcell" className={`cc-detail ${s.tone}`}>{s.detail}</div>}
          </div>;
        })}
        {!c.paths.length && <div role="row" className="crow"><div role="gridcell" className="cadd cmuted">No way in yet.</div></div>}
      </div>
      <button className="clink cpeek-open" onClick={() => openItem({ kind: "computer", id: c.id })}>
        Open {c.name}: passwords, notes and settings ›</button>
    </div>;
  };

  /** The computers on a box: which is on its cable (the radio), each one's page and Connect, and adding one. On the
   * box's page and in its preview in the list. */
  const boxRows = (k: KvmT) => {
    const manage = k.access === "manage";
    const through = (comps ?? []).flatMap((c) => c.paths.filter((p) => p.kind === "kvm" && p.kvm_id === k.id).map((p) => ({ c, p })));
    const canCable = (cid: string) => manage || managed.has(cid);
    const canAdd = manage || k.access === "attach";
    const empty = !k.computers.length && !k.others;
    return <>
      <div className="ctree" role="grid" aria-label={`Computers on ${k.name}`}>
        {k.computers.map((x) => {
          const xk = `kc:${k.id}:${x.id}`;
          const path = through.find(({ c }) => c.id === x.id)?.p;
          return <div key={x.id} role="row" className="crow">
            <div role="gridcell" className="cc-name">
              <label className="cradio" title={canCable(x.id) ? "The one on its cable now" : undefined}>
                <input type="radio" name={`cable-${k.id}`} checked={k.on_cable === x.id} aria-label={`${x.name} is on the cable`}
                  disabled={!canCable(x.id) || busy} onChange={() => void run(() => api.setCable(k.id, x.id), xk)} /></label>
              <button className="clink cc-way" onClick={() => openItem({ kind: "computer", id: x.id })}>{x.name} ›</button>
              {k.on_cable === x.id && <span className="ctag">on cable</span>}</div>
            <div role="gridcell" className="cc-addr" />
            <div role="gridcell" className="cc-sign" />
            <div role="gridcell">{path && <StatusCell s={pathStatus(path, me)} />}</div>
            <div role="gridcell" className="cc-acts"><Saved k={xk} />
              {path && <button className={`btn ${pathStatus(path, me).usable ? "primary" : "hov"}`}
                aria-label={`Connect to ${x.name}`} onClick={() => onConnect(path.id)}>Connect</button>}</div>
          </div>;
        })}
        {(k.on_cable === "other" || k.others > 0) && <div role="row" className="crow"><div role="gridcell" className="cadd cmuted">
          {k.others > 0 && `+ ${k.others} computer${k.others > 1 ? "s" : ""} not shared with you`}
          {k.on_cable === "other" && `${k.others > 0 ? " · " : ""}one of them is on its cable`}</div></div>}
        {empty && !canAdd && <div role="row" className="crow"><div role="gridcell" className="cadd cmuted">
          No computers on it yet.</div></div>}
        {canAdd && <div role="row" className="crow"><div role="gridcell" className="cadd">
          <button className="cadd-btn" onClick={() => open({ kind: "onbox", k })}>+ Add a computer on {k.name}</button>
          <span className="cmuted">{empty ? "nothing on it yet: " : ""}a new one, or one you already have</span></div></div>}
      </div>
      {panelAt(`kadd:${k.id}`)}
    </>;
  };
  const fromControl = (e: RMouseEvent) => !!(e.target as HTMLElement).closest("button, a, input, select, textarea, label");

  function computerList(): ReactNode {
    const needle = q.trim().toLowerCase();
    const shown = (comps ?? []).filter((c) => !needle || [c.name, c.description ?? "",
      ...c.paths.flatMap((p) => [p.via, KIND_LABEL[p.kind], address(p, kvms)])].join(" ").toLowerCase().includes(needle));
    if (comps && !comps.length) return <p className="note">No computers yet: add yours, or ask someone to share one.</p>;
    if (needle && !shown.length) return <p className="note">No computer matches.</p>;
    return <div className="clist" role="list" aria-label="Computers" ref={list}>
      {shown.map((c) => {
        const go1 = () => openItem({ kind: "computer", id: c.id });
        const first = c.paths[0], s = summary(c, me), d = firstLine(c.description);
        const owner = c.owner === me.email ? "yours" : `${who(c.owner || "admin")}'s`;
        const pk = `c:${c.id}`;
        return <div key={c.id} className={`cgroup ${peek.has(pk) ? "open" : ""}`}>
        <div role="listitem" className="citem" tabIndex={0} aria-label={`Open ${c.name}`}
          onClick={(e) => { if (!fromControl(e)) go1(); }} onKeyDown={(e) => rowKeys(e, go1, pk)}>
          {twist(pk, c.name)}
          <div className="ci-main">
            <div className="ci-name">{c.name}</div>
            <div className={`ci-desc ${d ? "" : "cmuted"}`}>{d || `${c.os === "macos" ? "macOS" : "Windows"} · ${owner}`}</div>
          </div>
          <div className="ci-ways">{c.paths.length
            ? c.paths.map((p) => <span key={p.id} className="cchip">{wayName(p, kvms)}</span>)
            : <span className="cmuted">—</span>}</div>
          <div><StatusCell s={s} /></div>
          <div className="ci-acts"><Saved k={`c:${c.id}`} />
            {first && <button className={`btn ${pathStatus(first, me).usable ? "primary" : "hov"}`}
              aria-label={`Connect to ${c.name}`} onClick={() => onConnect(first.id)}>Connect</button>}
            <span className="ci-go" aria-hidden="true">›</span></div>
        </div>
        {peek.has(pk) && waysPeek(c)}
        </div>;
      })}
    </div>;
  }

  function kvmList(): ReactNode {
    const needle = q.trim().toLowerCase();
    const shown = kvms.filter((k) => !needle || [k.name, k.description ?? "", String(k.settings.url ?? ""),
      ...k.computers.map((x) => x.name)].join(" ").toLowerCase().includes(needle));
    if (!kvms.length) return <p className="note">No KVM boxes yet.</p>;
    if (needle && !shown.length) return <p className="note">No KVM box matches.</p>;
    return <div className="clist" role="list" aria-label="KVM boxes" ref={list}>
      {shown.map((k) => {
        const go1 = () => openItem({ kind: "kvm", id: k.id });
        const d = firstLine(k.description);
        const cable = k.computers.find((x) => x.id === k.on_cable)?.name ?? (k.on_cable === "other" ? "another computer" : "");
        const n = k.computers.length + k.others;
        const pk = `k:${k.id}`;
        return <div key={k.id} className={`cgroup ${peek.has(pk) ? "open" : ""}`}>
        <div role="listitem" className="citem" tabIndex={0} aria-label={`Open ${k.name}`}
          onClick={(e) => { if (!fromControl(e)) go1(); }} onKeyDown={(e) => rowKeys(e, go1, pk)}>
          {twist(pk, k.name)}
          <div className="ci-main">
            <div className="ci-name">{k.name}</div>
            <div className={`ci-desc ${d ? "" : "cmuted mono"}`}>{d || String(k.settings.url ?? "").replace(/^https?:\/\//, "")}</div>
          </div>
          <div className="ci-ways">{n ? `${n} computer${n > 1 ? "s" : ""}` : <span className="cmuted">no computers yet</span>}</div>
          <div className="cmuted">{cable && `${cable} on its cable`}</div>
          <div className="ci-acts"><Saved k={`k:${k.id}`} /><span className="ci-go" aria-hidden="true">›</span></div>
        </div>
        {peek.has(pk) && <div className="cpeek">
          {boxRows(k)}
          <button className="clink cpeek-open" onClick={go1}>Open {k.name}: its login, notes and settings ›</button>
        </div>}
        </div>;
      })}
    </div>;
  }

  // ---------------------------------------------------------------------------------------- a computer's page
  function computerPage(c: ComputerT): ReactNode {
    const manage = c.access === "manage", key = `c:${c.id}`;
    const owner = c.owner === me.email ? "yours" : `${who(c.owner || "admin")}'s`;
    const role = manage ? (c.owner === me.email ? "" : " · you manage it") : " · shared with you";
    const shares = c.shares ?? [];
    const held = c.paths.find((p) => p.holder);
    const specs = valueSpecs(c);
    const boxes = [...new Set(c.paths.filter((p) => p.kind === "kvm" && p.kvm_id).map((p) => p.kvm_id!))];
    const set = specs.filter((v) => v.set && !v.own).map((v) => v.label);
    const own = specs.filter((v) => v.own).length;
    const secretsSummary = [set.length ? set.join(", ") : manage ? "no password set" : "",
      own ? `${own} value${own > 1 ? "s" : ""} of its own` : ""].filter(Boolean).join(" · ");
    return <>
      <div className="chero">
        <div className="chero-title">{nameEditor(key, c.name, manage,
          (name) => api.saveComputer({ id: c.id, name, os: c.os, secrets: {} }))}<Saved k={key} /></div>
        <div className="chero-meta">{c.os === "macos" ? "macOS" : "Windows"} · {owner}{role}
          {manage && shares.length > 0 && <> · <button className="clink" onClick={() => open({ kind: "share", what: "computer", id: c.id, name: c.name, shares })}>
            shared with {shares.length === 1 ? who(shares[0].email) : shares.length}</button></>}</div>
        <div className="chero-acts"><StatusCell s={summary(c, me)} />
          {manage && <Menu label={`More for ${c.name}`} items={[
            ["Change system (Windows, macOS)…", () => open({ kind: "computer", c })],
            ["Share…", () => open({ kind: "share", what: "computer", id: c.id, name: c.name, shares })],
            ["Same PC as… (merge)", () => open({ kind: "merge", c })],
            ...(held ? [[`Release (${who(held.holder!)})`, () => void run(() => api.releasePath(held.id))] as MenuItem] : []),
            ["Remove", () => confirmRun(`Remove ${c.name} with its ways in, chats and runs?`, () => api.removeComputer(c.id)), true],
          ]} />}</div>
      </div>
      {panelAt(key)}
      {describe("computer", c.id, c.description, manage, "what this PC is, where it stands, whose it is, its quirks")}

      <Section title="Ways in">
        <div className="ctree" role="grid" aria-label={`${c.name}'s ways in`}>
          {c.paths.map((p, i) => {
            const pk = `p:${p.id}`, s = pathStatus(p, me);
            const editPath = () => open({ kind: "path", c, p, way: p.kind });
            const [learnt, learntFull] = learnedText(p.learned);
            const fix = () => {
              if (p.kind === "kvm" && p.kvm_id) { openItem({ kind: "kvm", id: p.kvm_id }); unfold("login"); setEditing(`kb:${p.kvm_id}`); }
              else { unfold("secrets"); edit(`v:${c.id}:${PATH_SECRET[p.kind]}`); }
            };
            return <Fragment key={p.id}>
              <div role="row" className="crow">
                <div role="gridcell" className="cc-name">
                  {p.kind === "kvm" && p.kvm_id
                    ? <button className="clink cc-way" title={`Open ${wayName(p, kvms)}: its login, description and computers`}
                        onClick={() => openItem({ kind: "kvm", id: p.kvm_id! })}>{wayName(p, kvms)} ›</button>
                    : <span className="cc-way">{wayName(p, kvms)}</span>}
                  {i === 0 && c.paths.length > 1 && <span className="ctag">default</span>}
                  {p.id === current && <span className="ctag">last used</span>}</div>
                <div role="gridcell" className="cc-addr mono">{address(p, kvms)}</div>
                <div role="gridcell" className="cc-sign" title={learntFull}>{[wayFacts(p), learnt].filter(Boolean).join(" · ")}</div>
                <div role="gridcell"><StatusCell s={s} /></div>
                <div role="gridcell" className="cc-acts"><Saved k={pk} />
                  {manage && <button className="btn quiet hov" onClick={editPath}>Edit</button>}
                  {manage && <Menu label={`More for ${c.name} via ${p.via}`} items={[
                    ...(p.holder ? [[`Release (${who(p.holder)})`, () => void run(() => api.releasePath(p.id))] as MenuItem] : []),
                    ...(p.learned?.length ? [[`Forget what was learned (${learnt})`, () => confirmRun(
                      `Forget the pointer and wheel the harness learned on ${c.name} via ${p.via}? It learns them again on its next session.`,
                      () => api.resetLearned(p.id))] as MenuItem] : []),
                    ...(c.paths.length > 1 ? [
                      ["Split off (it reaches another PC)", () => open({ kind: "split", c, p })] as MenuItem,
                      ["Remove this way in", () => confirmRun(`Remove ${c.name}'s ${p.via} way in? Its chats stay with ${c.name}.`,
                        () => api.removePath(p.id)), true] as MenuItem] : []),
                  ]} />}
                  {s.refused && manage
                    ? <button className="btn" onClick={fix}>Fix sign-in</button>
                    : <button className={`btn ${s.usable ? "primary" : "hov"}`} onClick={() => onConnect(p.id)}>Connect</button>}
                </div>
                {s.detail && <div role="gridcell" className={`cc-detail ${s.tone}`}>{s.detail}</div>}
              </div>
              {panelAt(pk)}
            </Fragment>;
          })}
          {!c.paths.length && <div role="row" className="crow"><div role="gridcell" className="cadd cmuted">No way in yet.</div></div>}
          {manage && <div role="row" className="crow">
            <div role="gridcell" className="cadd"><span className="cadd-lead">+ Add a way in:</span>
              {ADDABLE.map(([kind, label]) => <button key={kind} className="cadd-btn" onClick={() => open({ kind: "path", c, way: kind })}>
                {label}</button>)}</div></div>}
        </div>
        {panelAt(`add:${c.id}`)}
      </Section>

      {(specs.length > 0 || manage || boxes.length > 0) && <Section title="Passwords & secrets" shut={shut.has("secrets")}
        onFold={() => fold("secrets")} summary={secretsSummary}>
        <div className="ctree" role="grid" aria-label={`${c.name}'s passwords and secrets`}>
          {specs.map((v) => <div key={v.key} role="row" className="crow">
            <ValueCells spec={v} editing={editing === v.key} onEdit={(on) => edit(on ? v.key : null)}
              onDone={(msg) => done(v.key, msg)} saved={savedMsg(v.key)} /></div>)}
          {manage && (() => {
            const nk = `vn:${c.id}`;
            return <div role="row" className="crow">{editing === nk
              ? <ValueCells editing onEdit={(on) => edit(on ? nk : null)} onDone={(msg) => done(nk, msg)} spec={{
                  key: nk, label: "", name: "", own: true, secret: true, set: false, usedBy: "", canEdit: true, canShow: false,
                  save: ({ name, value, secret }) => api.saveValue(c.id, name, { value, secret }) }} />
              : <div role="gridcell" className="cadd">
                  <button className="cadd-btn" onClick={() => edit(nk)}>+ Add a value…</button>
                  <span className="cmuted">a password, PIN or note for this PC: the agent types a secret by its name, and reads a plain one</span>
                  <Saved k={nk} /></div>}</div>;
          })()}
          {boxes.map((kid) => {
            const k = kvms.find((x) => x.id === kid);
            return k && <div key={kid} role="row" className="crow"><div role="gridcell" className="cadd cmuted">
              {k.name}'s own login is the box's, kept on{" "}
              <button className="clink" onClick={() => { openItem({ kind: "kvm", id: kid }); unfold("login"); }}>{k.name}'s page ›</button>
            </div></div>;
          })}
        </div>
      </Section>}
    </>;
  }

  // ----------------------------------------------------------------------------------------- a KVM box's page
  function kvmPage(k: KvmT): ReactNode {
    const manage = k.access === "manage", key = `k:${k.id}`;
    const through = (comps ?? []).flatMap((c) => c.paths.filter((p) => p.kind === "kvm" && p.kvm_id === k.id).map((p) => ({ c, p })));
    const held = through.find(({ p }) => p.holder);
    const st: Status | null = held ? pathStatus(held.p, me) : k.others ? null : { tone: "ok", text: "not in use", usable: true };
    const shares = k.shares ?? [];
    const url = String(k.settings.url ?? "").replace(/^https?:\/\//, "");
    const lk = `kb:${k.id}`;
    const login: ValueSpec = { key: lk, label: "User and password", usedBy: "for every computer on it", secret: true,
      set: k.secrets_set.includes("password"), user: String(k.settings.user || "admin"), canEdit: true, canShow: true,
      fetch: async (why) => (await api.kvmPassword(k.id, why)).value,
      save: ({ value, user }) => api.saveKvm({ id: k.id, name: k.name, settings: { ...k.settings, user: user || "admin" },
        secrets: { password: value } }) };
    return <>
      <div className="chero">
        <div className="chero-title">{nameEditor(key, k.name, manage,
          (name) => api.saveKvm({ id: k.id, name, settings: k.settings, secrets: {} }))}<Saved k={key} /></div>
        <div className="chero-meta">{url ? <span className="mono">{url}</span> : "a KVM box"}
          {k.access === "attach" && " · you may plug your computers into it"}
          {manage && shares.length > 0 && <> · <button className="clink" onClick={() => open({ kind: "share", what: "kvm", id: k.id, name: k.name, shares })}>
            shared with {shares.length === 1 ? who(shares[0].email) : shares.length}</button></>}</div>
        <div className="chero-acts"><StatusCell s={st} />
          {manage && <Menu label={`More for ${k.name}`} items={[
            ["Change its address…", () => open({ kind: "kvm", k })],
            ["Share…", () => open({ kind: "share", what: "kvm", id: k.id, name: k.name, shares })],
            ...(k.on_cable && k.on_cable !== "other" ? [["Cable: not said", () => void run(() => api.setCable(k.id, ""), key)] as MenuItem] : []),
            ["Remove", () => confirmRun(`Remove ${k.name}?`, () => api.removeKvm(k.id)), true],
          ]} />}</div>
      </div>
      {panelAt(key)}
      {describe("kvm", k.id, k.description, manage, "where it is, which cables, its quirks")}

      <Section title="Login" shut={shut.has("login")} onFold={() => fold("login")}
        summary={manage ? `${String(k.settings.user || "admin")} · ${k.secrets_set.includes("password") ? "password set" : "no password"}` : ""}>
        <div className="ctree" role="grid" aria-label={`${k.name}'s login`}>
          {manage
            ? <div role="row" className="crow"><ValueCells spec={login} editing={editing === lk}
                onEdit={(on) => edit(on ? lk : null)} onDone={(msg) => done(lk, msg)} saved={savedMsg(lk)} /></div>
            : <div role="row" className="crow"><div role="gridcell" className="cadd cmuted">
                Its login is kept by its managers: the harness signs in with it for every computer on the box.</div></div>}
        </div>
      </Section>

      <Section title="Computers on it" action={(manage || k.access === "attach") &&
        <button className="btn primary" onClick={() => open({ kind: "onbox", k })}>+ Add a computer</button>}>{boxRows(k)}</Section>
    </>;
  }

  const page = top?.kind === "computer" ? comps?.find((c) => c.id === top.id) : undefined;
  const box = top?.kind === "kvm" ? kvms.find((k) => k.id === top.id) : undefined;
  const newOne = panel && anchor(panel) === "new";

  return (
    <div className="page"><div className="page-inner cpage">
      {!top ? <>
        <div className="page-head chead">
          <h1>{rootLabel}</h1>
          <div className="cseg" role="group" aria-label="Show">
            <button aria-pressed={loc.root === "computers"} onClick={() => go({ root: "computers", trail: [] })}>Computers</button>
            <button aria-pressed={loc.root === "kvms"} onClick={() => go({ root: "kvms", trail: [] })}>KVM boxes</button>
          </div>
          <span className="grow" />
          <input className="field csearch" type="search" placeholder="Search" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          {loc.root === "computers"
            ? <button className="btn" onClick={() => open({ kind: "computer" })}>+ Add computer</button>
            : <button className="btn" onClick={() => open({ kind: "kvm" })}>+ Add KVM box</button>}
        </div>
        <p className="lede">{loc.root === "computers"
          ? "Click a computer for its ways in, passwords and notes. One person drives a computer at a time, whichever way in."
          : "Click a box for its login, notes and the computers on it. A box shows one computer at a time: the one on its cable."}</p>
        {newOne && panel && <div className="cpanel-top">{renderPanel(panel)}</div>}
        {comps === null && !err && <p className="note" role="status">Loading…</p>}
        {comps !== null && (loc.root === "computers" ? computerList() : kvmList())}
      </> : <>
        <nav className="ccrumbs" aria-label="Where you are">
          <button className="crumb" onClick={() => go({ root: loc.root, trail: [] })}>{rootLabel}</button>
          {loc.trail.map((c, i) => <Fragment key={`${c.kind}:${c.id}`}>
            <span className="crumb-sep" aria-hidden="true">›</span>
            <button className="crumb" aria-current={i === loc.trail.length - 1 ? "location" : undefined}
              onClick={() => go({ root: loc.root, trail: loc.trail.slice(0, i + 1) })}>{crumbName(c)}</button>
          </Fragment>)}
        </nav>
        {comps === null && !err && <p className="note" role="status">Loading…</p>}
        {page && computerPage(page)}
        {box && kvmPage(box)}
      </>}
      {err && <div className="error-line" role="alert">{err}</div>}
    </div></div>
  );
}

/** A titled part of a page. With `onFold` its whole title bar opens and closes it; folded, it says what it holds. */
function Section({ title, summary, shut, onFold, action, children }: {
  title: string; summary?: string; shut?: boolean; onFold?: () => void; action?: ReactNode; children: ReactNode;
}) {
  return <section className={`csec ${shut ? "shut" : ""}`} aria-label={title}>
    {onFold
      ? <button className="csec-head" aria-expanded={!shut} onClick={onFold}>
          <span className="csec-chev" aria-hidden="true">{shut ? "▸" : "▾"}</span>
          <span className="csec-title">{title}</span>
          {shut && summary && <span className="csec-sum">{summary}</span>}
          <span className="grow" /><span className="csec-hint">{shut ? "show" : "hide"}</span></button>
      : <div className="csec-head"><span className="csec-title">{title}</span><span className="grow" />{action}</div>}
    {!shut && <div className="csec-body">{children}</div>}
  </section>;
}

/** A computer's or a KVM box's description: free text, blank at first. Click it (or ✎) to edit; Cmd/Ctrl+Enter
 * saves, Esc cancels. Line breaks are kept. */
function Description({ text, canEdit, editing, what, onEdit, onSave, saved }: {
  text: string; canEdit: boolean; editing: boolean; what: string; saved: string | null;
  onEdit: (on: boolean) => void; onSave: (text: string) => Promise<void>;
}) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (editing) { setV(text); setErr(""); } }, [editing]);   // from the text as it is when editing starts
  const save = async () => {
    if (busy) return;
    if (v.trim() === text.trim()) { onEdit(false); return; }
    setBusy(true);
    try { await onSave(v); onEdit(false); } catch (e) { setErr(String((e as Error).message)); } finally { setBusy(false); }
  };
  if (editing) return <div className="cdesc editing">
    <textarea className="field cdesc-in" autoFocus aria-label="Description" value={v} maxLength={4000} placeholder={what}
      rows={Math.min(14, Math.max(4, v.split("\n").length + 1))} onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onEdit(false); }
        else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); }
      }} />
    <div className="cdesc-foot">
      <span className="cmuted">The agent reads it too. {navigator.platform.startsWith("Mac") ? "⌘" : "Ctrl"}+Enter saves, Esc cancels.</span>
      {err && <span className="cv-err" role="alert">{err}</span>}
      <span className="grow" />
      <button type="button" className="btn quiet" onClick={() => onEdit(false)}>Cancel</button>
      <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
    </div>
  </div>;
  if (!text) return canEdit
    ? <button className="cdesc-add" onClick={() => onEdit(true)}>+ Add a description<span className="cmuted"> · {what}</span>
        {saved && <span className="csaved" role="status"> {saved}</span>}</button>
    : null;
  return <div className="cdesc">
    <div className={`cdesc-text ${canEdit ? "editable" : ""}`} onClick={canEdit ? () => onEdit(true) : undefined}
      title={canEdit ? "Click to change" : undefined}>{text}</div>
    <div className="cdesc-acts">{saved && <span className="csaved" role="status">{saved}</span>}
      {canEdit && <button className="btn quiet" aria-label="Edit the description" title="Edit" onClick={() => onEdit(true)}>✎</button>}</div>
  </div>;
}

function StatusCell({ s }: { s: Status | null }) {
  if (!s) return null;
  return <span className={`cstatus ${s.tone}`}><span className={`dot ${s.tone === "idle" ? "" : s.tone}`} aria-hidden="true" />
    <span className="cst-text">{s.text}</span></span>;
}

type MenuItem = [string, () => void, boolean?];

function Menu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);
  if (!items.length) return null;
  return (
    <div className="cmenu-wrap" ref={ref}>
      <button className="btn quiet hov" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>⋯</button>
      {open && <div role="menu" className="cmenu" onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } }}>
        {items.map(([text, act, danger]) => <button role="menuitem" key={text} className={danger ? "danger" : ""}
          onClick={() => { setOpen(false); act(); }}>{text}</button>)}
      </div>}
    </div>
  );
}

type FormProps = { busy: boolean; error: string; onCancel: () => void };

function Panel({ title, ok, busy, error, onCancel, onSave, saveLabel = "Save", note, children }: FormProps & {
  title: string; ok: boolean; onSave: () => void; saveLabel?: string; note?: ReactNode; children: ReactNode;
}) {
  return (
    <form className="cpanel" onSubmit={(e) => { e.preventDefault(); if (ok && !busy) onSave(); }}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } }}>
      <div className="cpanel-title">{title}</div>
      <div className="cfields">{children}</div>
      {note && <p className="note">{note}</p>}
      {error && <p className="error-line" role="alert">{error}</p>}
      <div className="cpanel-acts">
        <button type="button" className="btn quiet" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn primary" disabled={!ok || busy}>{busy ? "Saving…" : saveLabel}</button>
      </div>
    </form>
  );
}

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return <div className={`cfield ${wide ? "wide" : ""}`}><label><span className="clabel">{label}</span>{children}</label>
    {hint && <small>{hint}</small>}</div>;
}

function Secret({ label, set, value, onChange, hint }: { label: string; set: boolean; value: string; onChange: (v: string) => void; hint?: string }) {
  return <Field label={label} hint={hint}><input className="field" type="password" autoComplete="new-password" value={value}
    placeholder={set ? "set · type to replace" : "not set"} onChange={(e) => onChange(e.target.value)} /></Field>;
}

function ComputerForm({ c, onSave, ...f }: FormProps & { c?: ComputerT; onSave: (name: string, os: string) => void }) {
  const [name, setName] = useState(c?.name ?? "");
  const [os, setOs] = useState(c?.os ?? "windows");
  return (
    <Panel {...f} title={c ? `Rename ${c.name}` : "Add a computer"} ok={!!name.trim()} saveLabel={c ? "Save" : "Add"}
      onSave={() => onSave(name.trim(), os)} note={!c && "Then add its ways in (a KVM box, VNC or Remote Desktop) in its rows."}>
      <Field label="Name"><input className="field" autoFocus value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="System"><select className="field" value={os} onChange={(e) => setOs(e.target.value)}>
        <option value="windows">Windows</option><option value="macos">macOS</option></select></Field>
    </Panel>
  );
}

function PathForm({ c, p, way, kvms, onSave, ...f }: FormProps & {
  c: ComputerT; p?: PathT; way: string; kvms: KvmT[];
  onSave: (kvmId: string, settings: Record<string, unknown>, secret: string) => void;
}) {
  const usable = kvms.filter((k) => k.access === "manage" || k.access === "attach" || k.id === p?.kvm_id);
  const init = (k: string) => { const v = p?.settings[k]; return v === undefined || v === null ? "" : Array.isArray(v) ? v.join("x") : String(v); };
  const [kvm, setKvm] = useState(p?.kvm_id || usable[0]?.id || "");
  const [host, setHost] = useState(init("host"));
  const [port, setPort] = useState(init("port"));
  const [user, setUser] = useState(p ? init("user") : way === "rdp" ? "Administrator" : "");   // a new RDP's usual account
  const [size, setSize] = useState(init("size"));
  const [typing, setTyping] = useState(init("type_event_ms"));
  const [secret, setSecret] = useState("");
  const remote = way === "vnc" || way === "rdp";
  const missing = remote && !host.trim() ? "Host is required."
    : remote && port.trim() && !/^\d+$/.test(port.trim()) ? "Port is a number."
    : way === "rdp" && !user.split("\\").pop()?.trim() ? "User is required (for example Administrator or DOMAIN\\name)."
    : way === "rdp" && size.trim() && !/^\d+\s*x\s*\d+$/i.test(size.trim()) ? "Size is WIDTHxHEIGHT, for example 1920x1080."
    : way === "kvm" && !kvm ? "Pick a KVM box: add one under Group by KVM box, or ask its owner to let you attach."
    : way === "kvm" && typing.trim() && !/^\d+(\.\d+)?$/.test(typing.trim()) ? "Typing is a number of milliseconds." : "";
  const save = () => {
    const s: Record<string, unknown> = { ...(p?.settings ?? {}) };
    if (way === "kvm") for (const k of ["url", "user", "verify_tls"]) delete s[k];   // the box's own, not this way in's
    const put = (k: string, v: unknown) => { if (v === "") delete s[k]; else s[k] = v; };
    if (remote) { put("host", host.trim()); put("port", port.trim() ? Number(port) : ""); }
    if (way === "rdp") {
      put("user", user.trim());
      put("size", size.trim() ? size.toLowerCase().replace(/\s/g, "").split("x").map(Number) : "");
    }
    if (way === "kvm") put("type_event_ms", typing.trim() ? Number(typing) : "");
    onSave(way === "kvm" ? kvm : "", s, secret);
  };
  const sk = PATH_SECRET[way];
  const title = `${c.name}: ${p ? "edit" : "add"} ${way === "kvm" ? "a KVM way in" : KIND_LABEL[way]}`;
  return (
    <Panel {...f} title={title} ok={!missing} saveLabel={p ? "Save" : "Add"} onSave={save} note={<>
      {way === "kvm" && "The KVM box's own login is the box's: it is under Values & secrets."}
      {way === "vnc" && "Classic VNC uses only the first 8 characters of its password and does not encrypt: use a private network or WireGuard."}
      {way === "rdp" && "The Windows account's own password, not the PIN. The same user and password as in Microsoft's Windows App."}
      {sk && p && " Its password is under Values & secrets."}
      {sk && !p && !c.secrets_set.includes(sk) && !secret && <span className="cwarn"> No password is set yet.</span>}
      {missing && <span className="error-line"> {missing}</span>}</>}>
      {way === "kvm" && <>
        <Field label="KVM box"><select className="field" autoFocus value={kvm} onChange={(e) => setKvm(e.target.value)}>
          {usable.length === 0 && <option value="">none you may use</option>}
          {usable.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select></Field>
        <Field label="Typing" hint="ms per key; empty = 20"><input className="field" inputMode="decimal" value={typing}
          placeholder="20" onChange={(e) => setTyping(e.target.value)} /></Field>
      </>}
      {remote && <>
        <Field label="Host"><input className="field" autoFocus value={host} placeholder="host or IP" onChange={(e) => setHost(e.target.value)} /></Field>
        <Field label="Port"><input className="field" inputMode="numeric" value={port} placeholder={way === "rdp" ? "3389" : "5900"}
          onChange={(e) => setPort(e.target.value)} /></Field>
      </>}
      {way === "rdp" && <Field label="User" hint="DOMAIN\name, name@domain or MicrosoftAccount\you@example.com">
        <input className="field" value={user} onChange={(e) => setUser(e.target.value)} /></Field>}
      {sk && !p && <Secret label={way === "rdp" ? "RDP password" : "VNC password"} set={c.secrets_set.includes(sk)} value={secret} onChange={setSecret} />}
      {way === "rdp" && <Field label="Size" hint="empty = 1920x1080"><input className="field" value={size} placeholder="1920x1080"
        onChange={(e) => setSize(e.target.value)} /></Field>}
      {way === "sim" && <p className="note">The offline simulator: nothing to set.</p>}
    </Panel>
  );
}

function KvmForm({ k, onSave, ...f }: FormProps & { k?: KvmT; onSave: (name: string, settings: Record<string, unknown>, password: string) => void }) {
  const [name, setName] = useState(k?.name ?? "");
  const [url, setUrl] = useState(String(k?.settings.url ?? ""));
  const [user, setUser] = useState(String(k?.settings.user ?? "admin"));
  const [password, setPassword] = useState("");
  return (
    <Panel {...f} title={k ? `Edit ${k.name}` : "Add a KVM box"} ok={!!name.trim() && !!url.trim()} saveLabel={k ? "Save" : "Add"}
      note={k && "Its login (user and password) is in its Login row."}
      onSave={() => onSave(name.trim(), { url: url.trim(), user: user.trim() || "admin", verify_tls: Boolean(k?.settings.verify_tls) }, password)}>
      <Field label="Name"><input className="field" autoFocus value={name} placeholder="KVM3" onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Address"><input className="field" value={url} placeholder="https://10.0.0.8" onChange={(e) => setUrl(e.target.value)} /></Field>
      {!k && <Field label="KVM user"><input className="field" value={user} onChange={(e) => setUser(e.target.value)} /></Field>}
      {!k && <Secret label="KVM password" set={false} value={password} onChange={setPassword} />}
    </Panel>
  );
}

/** A computer on this KVM box: a new one, named, with its system; or one you already have, attached. Either gets a
 * KVM way in through the box, and is put on its cable if it is on it now (ticked already for an empty box). */
function AddToBox({ k, mine, onNew, onAttach, ...f }: FormProps & {
  k: KvmT; mine: ComputerT[];
  onNew: (name: string, os: string, onCable: boolean) => void; onAttach: (cid: string, onCable: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [os, setOs] = useState("windows");
  const [other, setOther] = useState("");
  const [onCable, setOnCable] = useState(!k.computers.length && !k.others);
  return (
    <Panel {...f} title={`Add a computer on ${k.name}`} ok={!!name.trim() || !!other} saveLabel={other ? "Attach" : "Add"}
      onSave={() => (other ? onAttach(other, onCable) : onNew(name.trim(), os, onCable))}
      note={other ? `It gets a way in through ${k.name}; its other ways in stay.`
        : `It gets a way in through ${k.name}. Its Windows sign-in and description go on its own page.`}>
      <Field label="Name"><input className="field" autoFocus value={name} placeholder="Front desk PC"
        onChange={(e) => { setName(e.target.value); if (e.target.value) setOther(""); }} /></Field>
      <Field label="System"><select className="field" value={os} disabled={!!other} onChange={(e) => setOs(e.target.value)}>
        <option value="windows">Windows</option><option value="macos">macOS</option></select></Field>
      {mine.length > 0 && <Field label="…or one you already have" wide>
        <select className="field" value={other} onChange={(e) => { setOther(e.target.value); if (e.target.value) setName(""); }}>
          <option value="">none: a new computer</option>
          {mine.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
      <Field label="Cable" wide><span className="ccheck"><input type="checkbox" checked={onCable}
        onChange={(e) => setOnCable(e.target.checked)} /> it is on {k.name}'s cable now</span></Field>
    </Panel>
  );
}

function MergeForm({ c, comps, onSave, ...f }: FormProps & { c: ComputerT; comps: ComputerT[]; onSave: (other: string) => void }) {
  const others = comps.filter((x) => x.id !== c.id && x.access === "manage");
  const [other, setOther] = useState(others[0]?.id ?? "");
  const o = others.find((x) => x.id === other);
  return (
    <Panel {...f} title={`Same PC as ${c.name}`} ok={!!other} saveLabel="Merge" onSave={() => onSave(other)}
      note={o ? `${o.name}'s ${o.paths.map((p) => p.via).join(" and ") || "entry"} becomes another way into ${c.name}, with its chats, runs and
        learned pointer. ${c.name}'s name and passwords are kept; passwords it lacks come from ${o.name}.` : "No other computer you manage."}>
      <Field label="The other entry of this PC" wide><select className="field" autoFocus value={other} onChange={(e) => setOther(e.target.value)}>
        {others.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.paths.map((p) => p.via).join(", ") || "no way in"})</option>)}
      </select></Field>
    </Panel>
  );
}

function SplitForm({ p, onSave, ...f }: FormProps & { p: PathT; onSave: (name: string) => void }) {
  const [name, setName] = useState("");
  return (
    <Panel {...f} title={`Split ${p.computer}'s ${p.via} way in off`} ok={!!name.trim()} saveLabel="Split off" onSave={() => onSave(name.trim())}
      note={`It becomes a computer of its own, with its chats. Only its own login (${p.kind === "vnc" ? "VNC" : p.kind === "rdp" ? "RDP" : "none"}) goes along.`}>
      <Field label="Name of the PC it reaches" wide><input className="field" autoFocus value={name} onChange={(e) => setName(e.target.value)} /></Field>
    </Panel>
  );
}

function SharePanel({ panel, onClose, onShares }: {
  panel: Extract<PanelT, { kind: "share" }>; onClose: () => void; onShares: (s: ShareT[]) => void;
}) {
  const levels: [Access, string][] = panel.what === "computer"
    ? [["use", "use: see and drive it, their own chats"], ["manage", "manage: also edit it, its passwords and ways in, share it on"]]
    : [["attach", "attach: plug their own computers into it"], ["manage", "manage: also edit it and share it on"]];
  const [people, setPeople] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [level, setLevel] = useState<Access>(levels[0][0]);
  const [err, setErr] = useState("");
  useEffect(() => { api.people().then(setPeople).catch(() => undefined); }, []);
  const go = (p: Promise<ShareT[]>) => p.then((s) => { onShares(s); setErr(""); setEmail(""); }).catch((e) => setErr(String(e.message)));
  const shared = new Set(panel.shares.map((s) => s.email));
  return (
    <form className="cpanel" onSubmit={(e) => { e.preventDefault(); if (email) void go(api.share(panel.what, panel.id, email, level)); }}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}>
      <div className="cpanel-title">Share {panel.name}</div>
      {panel.shares.length > 0
        ? <ul className="cshares">{panel.shares.map((s) => <li key={s.email}><span>{s.email}</span><span className="cmuted">{s.level}</span>
            <button type="button" className="btn quiet danger" aria-label={`Stop sharing with ${s.email}`}
              onClick={() => void go(api.unshare(panel.what, panel.id, s.email))}>✕</button></li>)}</ul>
        : <p className="note">Not shared with anyone.</p>}
      <div className="cfields">
        <Field label="With"><select className="field" autoFocus value={email} onChange={(e) => setEmail(e.target.value)}>
          <option value="">someone who can sign in…</option>
          {people.filter((p) => !shared.has(p)).map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
        <Field label="May" wide><select className="field" value={level} onChange={(e) => setLevel(e.target.value as Access)}>
          {levels.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      </div>
      <p className="note">{panel.what === "computer"
        ? "Shared, it comes with all its ways in, even through someone else's KVM box. Nobody sees a password; stopping a share ends their live view at once."
        : "They can plug their own computers into it. They get neither the computers already on it nor its login."}</p>
      {err && <p className="error-line" role="alert">{err}</p>}
      <div className="cpanel-acts">
        <button type="button" className="btn quiet" onClick={onClose}>Done</button>
        <button type="submit" className="btn primary" disabled={!email}>Share</button>
      </div>
    </form>
  );
}
